import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { DsButtonVariants, Icons } from '@levelaccess/design-system';
import { Api, ApiQueryOption } from '@shared/constants/api';
import { errorMessagesNames } from '@shared/constants/errors';
import { SharedCommonUtility } from '@shared/utils/common.utility';

import { IE_REVEAL_EYE_SELECTOR, redirectUrl, UserwayRedirectType } from '@client/app/shared/constants';
import { IHttpErrorResponse } from '@client/app/interfaces/error.interface';
import { RoutingService } from '@client/app/services/routing.service';
import { TranslateService } from '@client/app/translate/translate.service';
import { UserService } from '@client/app/services/user.service';
import { FormService } from '@client/app/services/form.service';
import { CustomValidators } from '@client/app/services/helpers/form-custom-validators';
import { CommonUtility } from '@client/app/utility/common.utility';
import { ErrorMessageService } from '@client/app/services/error-message.service';
import { A11yService } from '@client/app/services/a11y.service';
import { ErrorHandlerService } from '@client/app/services/error-handler.service';
import { alertType } from '@client/app/constants/alert.constants';
import { AppConfigService } from '@client/app/services/app-config.service';
import { ModalService } from '@client/app/services/modal.service';
import { BannerType } from '@client/app/components/users/edit-user/edit-permissions-banner/edit-permissions-banner.component';
import { TenantService } from '@client/app/services/tenant.service';
import { IUserWaySSOLink, UserWayOrganizationAuthRequestTarget } from '@shared/interfaces/userway-integration.interface';
import { NotificationService } from '@client/app/services/notification.service';
import { NotificationPosition } from '@client/app/models/notification.model';
import { LastVisitedLocationService } from '@client/app/services/navigation/last-visited-location.service';
import { ILastVisitedLocation } from '@client/app/interfaces/last-visited.interface';
import { ILinkedPropertyData, LinkedPropertyUtility } from '@shared/utils/linked-property.utility';
import { AclSecurityAdapter } from '@client/app/services/acl.service';
import { AngularUtility } from '@client/app/utility/angular.utility';
import { UserAclService } from '@client/app/services/user-acl.service';

import { TenantRedirectModalComponent } from './tenant-redirect-modal/tenant-redirect-modal.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  private formValidationRequest: Subject<void>;

  public pageTitle: string;
  public isUserAuthenticated: boolean | null;
  public isMfaRequired: boolean | null;
  public passwordIsTooOldError: boolean = false;
  public loginPasswordResetRequiredError: boolean = false;
  public userEmailUnverifiedError: boolean = false;
  public authErrorMessage: string;
  public form: UntypedFormGroup;
  public formValidationRequest$: Observable<void>;
  public alertType: typeof alertType;
  public isPasswordShown: boolean;
  public usePasswordRevealButton: boolean;
  public BannerType: typeof BannerType = BannerType;
  public DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public Icons: typeof Icons = Icons;

  constructor(
    private router: Router,
    private appConfigService: AppConfigService,
    private routingService: RoutingService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private userService: UserService,
    private formService: FormService,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private errorMessageService: ErrorMessageService,
    private a11yService: A11yService,
    private errorHandlerService: ErrorHandlerService,
    private tenantService: TenantService,
    private notificationService: NotificationService,
    private element: ElementRef<Element>,
    private userAclService: UserAclService,
    private lastVisitedService: LastVisitedLocationService,
  ) {
    this.pageTitle = routingService.getPageTitle();
    this.subscriptions = new Subscription();
    this.formValidationRequest = new Subject<any>();
    this.formValidationRequest$ = this.formValidationRequest.asObservable();
    this.isUserAuthenticated = this.userService.isAuthenticated();
    this.isMfaRequired = this.userService.isMfaRequired();
    this.authErrorMessage = '';
    this.alertType = alertType;
    this.isPasswordShown = false;
    this.usePasswordRevealButton = true;

    this.subscribeToRouteChanges();

    const formConfig = {
      email: new UntypedFormControl(null, {
        validators: [CustomValidators.validateEmail, Validators.required],
        updateOn: 'change',
      }),
      password: new UntypedFormControl(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
    };

    this.form = this.formBuilder.group(formConfig);
  }

  private onPageTitleChange(pageTitle: string): void {
    this.pageTitle = pageTitle;
  }

  private subscribeToRouteChanges(): void {
    const changePageTitleSubscription: Subscription = this.routingService.pageTitle$.subscribe(this.onPageTitleChange.bind(this));

    this.subscriptions.add(changePageTitleSubscription);
  }

  private async redirectToUserWay(target: UserWayOrganizationAuthRequestTarget, userwayAccountCode?: string): Promise<void> {
    if (target === UserWayOrganizationAuthRequestTarget.WIDGET) {
      window.open(this.constructUserwayServerRedirectUrl(target, userwayAccountCode), '_self');
      return;
    }

    /**
     * TODO EAP-28501: Update TokenInterceptor to reject and handle rejection here
     * The call to generateUserWaySSOLink will result in a Forbidden response when the user does not have permission to userway_integration.create
     * The forbidden response does not appear to be accessible using either a piped catchError or subscribed error
     *
     * Using a promise here allows the call to be intercepted and rerouted as expected
     * The conversion to promise again does not reliably reject the error, but rather resolves to undefined
     * We must re-throw the error for handling within the catch
     */
    try {
      const res: IUserWaySSOLink = await this.getUserWaySSOLink(target, userwayAccountCode);
      const redirectLink: string = this.constructUserwaySuccessRedirectUrl(res);

      window.open(redirectLink, '_self');
    } catch {
      this.notificationService.error(this.translateService.instant('userway_login_error'), NotificationPosition.Toast, true);
      this.redirectToApp();
    }
  }

  private constructUserwayServerRedirectUrl(target: UserWayOrganizationAuthRequestTarget, userwayAccountCode?: string): string {
    const queryParams: URLSearchParams = this.getUserwaySuccessRedirectQueryParams();
    const userwayFailureRedirectUri: string | undefined =
      this.activatedRoute.snapshot.queryParams[ApiQueryOption.userwayFailureRedirectUri];

    if (SharedCommonUtility.notNullishOrEmpty(userwayFailureRedirectUri)) {
      queryParams.set(ApiQueryOption.userwayFailureRedirectUri, userwayFailureRedirectUri);
    }

    return this.tenantService.generateUserWaySSORedirectUrl(target, userwayAccountCode, queryParams);
  }

  private async getUserWaySSOLink(
    target: UserWayOrganizationAuthRequestTarget,
    userwayAccountCode?: string,
  ): Promise<IUserWaySSOLink> {
    const response: IUserWaySSOLink = await this.tenantService.generateUserWaySSOLink(target, userwayAccountCode).toPromise();

    if (SharedCommonUtility.isStringEmpty(response?.ssoLink)) {
      throw new Error('Unable to generate UserWay SSO link');
    }

    return response;
  }

  private async redirectToApp(overridePath?: string): Promise<boolean> {
    const returnToPath: string = overridePath || this.activatedRoute.snapshot.queryParams[redirectUrl.returnTo];

    if (typeof returnToPath === 'string' && !this.isUserwayRedirectType(returnToPath)) {
      return this.router
        .navigateByUrl(returnToPath)
        .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
    }

    return this.router
      .navigate([`/${Api.home}`], { replaceUrl: true })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private async redirectToAppOrExternalLink(): Promise<void> {
    const returnTo = this.activatedRoute.snapshot.queryParams[redirectUrl.returnTo];
    if (this.isUserwayRedirectType(returnTo)) {
      const target =
        returnTo === UserwayRedirectType.Widget
          ? UserWayOrganizationAuthRequestTarget.WIDGET
          : UserWayOrganizationAuthRequestTarget.DASHBOARD;
      const userwayAccountCode: string | undefined = this.activatedRoute.snapshot.queryParams[ApiQueryOption.userwayAccountCode];

      await this.redirectToUserWay(target, userwayAccountCode);

      return;
    }

    const hasReturnToQueryParam: boolean = SharedCommonUtility.notNullishOrEmpty(
      this.activatedRoute.snapshot.queryParams[redirectUrl.returnTo],
    );

    const lastVisitedLocation: ILastVisitedLocation = this.lastVisitedService.getLastVisitedLocation();
    if (lastVisitedLocation && !hasReturnToQueryParam) {
      const linkedProperties: ILinkedPropertyData = LinkedPropertyUtility.fromLinkedPropertyQueryParam(
        lastVisitedLocation.queryParams,
      );

      this.subscriptions.add(
        this.canAccessLastVisitedLocation$(linkedProperties.workspaceId, linkedProperties.digitalPropertyId).subscribe(
          async (hasAccess: boolean) => {
            if (hasAccess) {
              const queryString: string = new URLSearchParams(lastVisitedLocation.queryParams).toString();
              const lastVisitedPath: string = `${lastVisitedLocation.path}?${queryString}`;
              await this.redirectToApp(lastVisitedPath);
            } else {
              await this.redirectToApp();
            }
          },
        ),
      );
      return;
    }

    await this.redirectToApp();
  }

  private canAccessLastVisitedLocation$(workspaceId: string, digitalPropertyId: string): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean => {
        return adapter.useWorkspaceId(workspaceId).useDigitalPropertyId(digitalPropertyId).check();
      }),
      AngularUtility.shareRef(),
    );
  }

  private shouldAutoRedirectToTenant(response: IHttpErrorResponse): boolean {
    return (
      response.error?.app?.name === errorMessagesNames.ErrorLoginToTenantlessForbidden && response.error?.app?.params?.length >= 1
    );
  }

  private generateRedirectUrlForDomain(domain: string, baseUrl: string): string {
    const query: string = SharedCommonUtility.notNullish(this.activatedRoute.snapshot.queryParams[redirectUrl.returnTo])
      ? decodeURIComponent(this.activatedRoute.snapshot.queryParams[redirectUrl.returnTo])
      : '';
    return `https://${domain}.${baseUrl}${query}`;
  }

  private async errorAuthentication(response: IHttpErrorResponse): Promise<void> {
    this.form.enable();

    if (response.url === null) {
      this.authErrorMessage = this.translateService.instant('login_unable_to_authenticate');
      return;
    }

    if (this.shouldAutoRedirectToTenant(response)) {
      await this.appConfigService.fetchConfig();
      const redirectModalComponent: TenantRedirectModalComponent = this.modalService.open(TenantRedirectModalComponent).instance;
      redirectModalComponent.redirectUrls = (response.error.app.params[0] as string).split(',').map((domain: string) => {
        const trimmedDomain: string = domain.trim();
        return {
          url: this.generateRedirectUrlForDomain(trimmedDomain, this.appConfigService.getTenantBaseUrl()),
          domain: trimmedDomain,
        };
      });
      this.subscriptions.add(
        redirectModalComponent.container.onClose$
          .pipe(withLatestFrom(redirectModalComponent.selectedUrl$))
          .subscribe(([_, targetUrl]: [void, string]) => {
            CommonUtility.navigateToExternalUrl(targetUrl);
          }),
      );

      return;
    }

    if (CommonUtility.extractHTTPErrorName(response) === errorMessagesNames.ErrorUserEmailUnverified) {
      this.userEmailUnverifiedError = true;
      this.a11yService.setMessage(this.translateService.instant('verify_email_to_login'));
      return;
    }

    if (CommonUtility.extractHTTPErrorName(response) === errorMessagesNames.PasswordIsTooOld) {
      this.passwordIsTooOldError = true;
      this.a11yService.setMessage(this.translateService.instant('you_need_to_reset_your_password'));
      return;
    }

    if (CommonUtility.extractHTTPErrorName(response) === errorMessagesNames.LoginPasswordResetRequired) {
      this.loginPasswordResetRequiredError = true;
      this.a11yService.setMessage(this.translateService.instant('you_need_to_reset_your_password'));
      return;
    }

    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);

    if (typeof errorMessage === 'string' && response.status > 0) {
      this.authErrorMessage = errorMessage;
    } else {
      this.authErrorMessage = this.translateService.instant('login_unable_to_authenticate');
    }

    this.a11yService.setMessage(this.authErrorMessage);
  }

  private async onAuthenticationChange(authenticated: boolean): Promise<void> {
    this.isUserAuthenticated = authenticated;

    if (authenticated) {
      await this.redirectToAppOrExternalLink();
    }
  }

  private async onMfaRequiredChange(isMfaRequired: boolean): Promise<void> {
    this.isMfaRequired = isMfaRequired;
  }

  public setPasswordVisibility(visible: boolean): void {
    this.isPasswordShown = visible;
  }

  public onUserUnverifiedEmailBannerClose(): void {
    this.userEmailUnverifiedError = false;
  }

  public onSubmit(): void {
    this.formValidationRequest.next();
    this.authErrorMessage = '';

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    this.form.disable();

    this.subscriptions.add(
      this.userService.logIn(this.form.value.email, this.form.value.password).subscribe({
        error: this.errorAuthentication.bind(this),
      }),
    );
  }

  public isFieldValid(field: string): boolean {
    return this.formService.isFieldValid(field, this.form);
  }

  private isUserwayRedirectType(returnTo: string): returnTo is UserwayRedirectType {
    return Object.values(UserwayRedirectType).includes(returnTo as UserwayRedirectType);
  }

  private constructUserwaySuccessRedirectUrl(res: IUserWaySSOLink): string {
    const url = new URL(res.ssoLink);
    const additionalParams = this.getUserwaySuccessRedirectQueryParams();

    additionalParams.forEach((value: string, key: string) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  }

  private getUserwaySuccessRedirectQueryParams(): URLSearchParams {
    const queryParams = new URLSearchParams();
    const userwayState: string | undefined = this.activatedRoute.snapshot.queryParams[ApiQueryOption.userwayState];
    if (SharedCommonUtility.notNullishOrEmpty(userwayState)) {
      queryParams.set(ApiQueryOption.requestId, userwayState);
    }

    const callerOrigin: string | undefined = this.activatedRoute.snapshot.queryParams[ApiQueryOption.callerOrigin];
    if (SharedCommonUtility.notNullishOrEmpty(callerOrigin)) {
      queryParams.set(ApiQueryOption.callerOrigin, callerOrigin);
    }

    return queryParams;
  }

  public onResendActivationLink(): void {
    const onSuccess = (): void => {
      const extras: NavigationExtras = {
        state: {
          isResendDone: true,
          hasResendFailed: false,
        },
      };

      this.router
        .navigate([`/${Api.activation_link}`], extras)
        .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
    };

    const onError = (response: HttpErrorResponse): void => {
      const extras: NavigationExtras = {
        state: {
          isResendDone: false,
          hasResendFailed: true,
          errorMessage: response.error.message,
        },
      };

      this.router
        .navigate([`/${Api.activation_link}`], extras)
        .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
    };

    this.subscriptions.add(this.userService.resendVisitorActivationLink(this.form.value.email).subscribe(onSuccess, onError));
  }

  public async ngOnInit(): Promise<void> {
    if (this.appConfigService.isUsingExternalIdp()) {
      await this.redirectToAppOrExternalLink();
    }

    await this.userService.getAuthStatus();

    this.isMfaRequired = this.userService.isMfaRequired();
    this.isUserAuthenticated = this.userService.isAuthenticated();

    this.usePasswordRevealButton = CommonUtility.isCSSselectorSupported(IE_REVEAL_EYE_SELECTOR) === false;

    const isAuthenticatedSubscription: Subscription = this.userService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe({
      next: this.onAuthenticationChange.bind(this),
    });

    const isMfaRequiredSubscription: Subscription = this.userService.isMfaRequired$.pipe(distinctUntilChanged()).subscribe({
      next: this.onMfaRequiredChange.bind(this),
    });

    this.subscriptions.add(isAuthenticatedSubscription);
    this.subscriptions.add(isMfaRequiredSubscription);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
